// Utility functions for ChatPane component
import type { ActionChoice, DocumentReference, Message } from './chat-pane.types'

/** User message text when flushing the agentic Continue CTA (`contentOverride === 'continue'`). */
export const AGENTIC_CONTINUE_USER_TEXT =
  'Continue from where you stopped. Use context and tool results you already have; avoid repeating the same searches or fetches unless necessary.'

export function isAgenticContinueUserText(content: string): boolean {
  const t = content.trim()
  return t === AGENTIC_CONTINUE_USER_TEXT || t.startsWith('Continue from where you stopped.')
}

/**
 * Legacy large-deletion guard emitted Force apply / Use patch / Cancel. Chat now uses
 * Review modal Accept only - hide that trio so it does not stack on pending proposed edits.
 */
export function filterStaleLargeDeletionActionChoices<
  T extends { label: string },
>(choices: T[] | undefined | null): T[] {
  if (!choices?.length) return []
  const labels = new Set(choices.map((c) => c.label))
  if (!labels.has('Force apply anyway') || !labels.has('Use patch instead')) {
    return choices
  }
  return choices.filter(
    (c) =>
      c.label !== 'Force apply anyway' &&
      c.label !== 'Use patch instead' &&
      c.label !== 'Cancel',
  )
}

/**
 * Accept / apply / keep of a patch or diff is the review card (Keep, Undo, Diff).
 * A chat CTA with that label only sends another message.
 */
const PROPOSED_EDIT_ACCEPT_CHOICE_RE =
  /\b(accept|apply|keep)\b[\s\S]{0,80}\b(patch(?:es)?|diffs?|edits?)\b|\b(patch(?:es)?|diffs?|edits?)\b[\s\S]{0,80}\b(accept|apply|keep)\b/i

export function isProposedEditAcceptChoiceLabel(label: string | undefined | null): boolean {
  return PROPOSED_EDIT_ACCEPT_CHOICE_RE.test(String(label || '').trim())
}

export function filterProposedEditAcceptChoices<T extends { label: string }>(
  choices: T[] | undefined | null,
): T[] {
  if (!choices?.length) return []
  return choices.filter((c) => !isProposedEditAcceptChoiceLabel(c.label))
}

/**
 * Legacy agentic-loop appended a deterministic pause notice into assistant content when the
 * tool/round cap hit. Newer builds use SSE `cappedAt` + the inline Continue banner only.
 * Infer from content so older history still shows the CTA after reload.
 */
export function inferCappedAtFromAssistantContent(
  content: string | undefined | null,
): 'max_tools' | 'max_rounds' | 'incomplete_write' | undefined {
  if (!content) return undefined
  // Em dash or ASCII hyphen; match legacy programmatic pause from agentic-loop.js
  if (/Paused\s+[—\-]\s+tool limit reached/i.test(content)) return 'max_tools'
  if (/Paused\s+[—\-]\s+round limit reached/i.test(content)) return 'max_rounds'
  if (/Stopped after \d+ tool calls \(safety cap\)/i.test(content)) return 'max_tools'
  if (/Stopped after \d+ model rounds \(safety cap\)/i.test(content)) return 'max_rounds'
  if (/Stopped before updating the document/i.test(content)) return 'incomplete_write'
  if (/The reply was cut off/i.test(content)) return 'incomplete_write'
  return undefined
}

/**
 * Strip legacy harness pause copy so it does not sit in the assistant markdown body
 * next to real reply text. Continue UX lives in the cappedAt banner only.
 */
export function stripAgenticPauseProse(content: string | undefined | null): string {
  if (!content) return ''
  return content
    .replace(
      /\n*\s*Paused\s+[—\-]\s+tool limit reached[^\n]*(?:\n|$)/gi,
      '\n',
    )
    .replace(
      /\n*\s*Paused\s+[—\-]\s+round limit reached[^\n]*(?:\n|$)/gi,
      '\n',
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Agentic cap banner: full CTA, compact Continuing..., or hidden. */
export type CapBannerPhase = 'full' | 'compact' | 'hidden'

/**
 * Derive cap-banner chrome from stream props so remount/reload cannot resurrect
 * a clickable Continue while that continue is already in flight.
 */
export function resolveCapBannerPhase(opts: {
  cappedAt?: 'max_rounds' | 'max_tools' | 'incomplete_write'
  hasLaterAssistant: boolean
  isChatStreaming: boolean
}): CapBannerPhase {
  if (!opts.cappedAt) return 'hidden'
  if (opts.hasLaterAssistant) return 'hidden'
  if (opts.isChatStreaming) return 'compact'
  return 'full'
}

/** Spinner under the latest assistant while Stop is showing and Continue is not already compact. */
export function shouldShowAssistantStreamWorking(opts: {
  isChatStreaming: boolean
  hasLaterAssistant: boolean
  capBannerPhase: CapBannerPhase
  isTyping?: boolean
}): boolean {
  if (opts.isTyping) return false
  return (
    opts.isChatStreaming &&
    !opts.hasLaterAssistant &&
    opts.capBannerPhase !== 'compact'
  )
}

/**
 * Do not replace in-memory messages with a history fetch while this conversation's
 * stream is live (Continue click was remounting the pane and restoring cappedAt).
 */
export function shouldSkipHostedHistoryReload(opts: {
  streamInFlight: boolean
  streamingConversationId: string | null | undefined
  hostedLinkedConversationId: string | null | undefined
}): boolean {
  if (!opts.streamInFlight) return false
  // First send: persist creates the conversation/thread before React state
  // lands both ids. Do not fetch-replace the live user bubble in that gap.
  if (!opts.streamingConversationId || !opts.hostedLinkedConversationId) return true
  return opts.streamingConversationId === opts.hostedLinkedConversationId
}

/**
 * First send promotes `new-*` to a persisted thread while the stream is live.
 * Clearing the body on that switch drops the user bubble; history reload then
 * skips because shouldSkipHostedHistoryReload is true until the stream ends.
 *
 * `streamInFlight` must be true from the moment send is committed (before
 * persist), not only after AbortController exists. POST /conversation calls
 * ensureAgentChatThread during that await.
 */
export function shouldKeepLiveStreamOnHostedThreadSwitch(opts: {
  streamInFlight: boolean
  /** Send already painted a user turn, even if streamInFlight has not flipped yet. */
  hasOptimisticUserTurn?: boolean
  previousHostedThreadId: string | null | undefined
  streamingConversationId: string | null | undefined
  hostedLinkedConversationId: string | null | undefined
}): boolean {
  if (!opts.streamInFlight && !opts.hasOptimisticUserTurn) return false
  // new-* promote, or first bind from an empty agent panel (hostedThreadId was unset).
  if (!opts.previousHostedThreadId || opts.previousHostedThreadId.startsWith('new-')) {
    return true
  }
  return Boolean(
    opts.streamingConversationId &&
      opts.streamingConversationId === opts.hostedLinkedConversationId,
  )
}

/** Empty new-chat welcome. A committed send must not fall back here if the list is briefly cleared. */
export function shouldShowChatFirstHome(opts: {
  loadingConversation: boolean
  messageCount: number
  blankNewHostedElsewhere: boolean
  turnCommitted: boolean
}): boolean {
  if (opts.turnCommitted) return false
  if (opts.loadingConversation) return false
  if (opts.blankNewHostedElsewhere) return false
  return opts.messageCount === 0
}

/** Full-pane spinner while a hosted thread resolves. Hide it once a new-* chat has been promoted. */
export function shouldCoverHostedThreadResolving(opts: {
  resolving: boolean
  sawNewThreadPlaceholder: boolean
}): boolean {
  if (!opts.resolving) return false
  return !opts.sawNewThreadPlaceholder
}

const heldUserTurns = new Map<string, Message>()
const promotedFromNewThreadIds = new Set<string>()

/** Keep the sent prompt across a new-* -> real thread id remount. */
export function rememberHeldUserTurn(
  threadId: string | null | undefined,
  message: Message,
): void {
  if (!threadId) return
  heldUserTurns.set(threadId, message)
}

export function rekeyHeldUserTurn(fromThreadId: string, toThreadId: string): void {
  const message = heldUserTurns.get(fromThreadId)
  if (!message) return
  heldUserTurns.set(toThreadId, message)
  heldUserTurns.delete(fromThreadId)
}

export function peekHeldUserTurn(threadId: string | null | undefined): Message | null {
  if (!threadId) return null
  return heldUserTurns.get(threadId) ?? null
}

export function clearHeldUserTurn(threadId: string | null | undefined): void {
  if (!threadId) return
  heldUserTurns.delete(threadId)
}

export function markPromotedFromNewThread(toThreadId: string): void {
  promotedFromNewThreadIds.add(toThreadId)
}

export function wasPromotedFromNewThread(threadId: string | null | undefined): boolean {
  if (!threadId) return false
  return threadId.startsWith('new-') || promotedFromNewThreadIds.has(threadId)
}

/** Align a user turn with the top of the scroller so the question stays on screen. */
export function scrollLatestUserTurnToTop(container: HTMLElement, messageIndex: number): boolean {
  const target = container.querySelector(`[data-message-index="${messageIndex}"]`)
  if (!(target instanceof HTMLElement)) return false
  const top =
    target.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  container.scrollTop = Math.max(0, top)
  return true
}

/** Restore user turns when a thread promote wiped them mid-stream. */
export function prependMissingUserTurns(live: Message[], sent: Message[]): Message[] {
  const hasVisibleUser = live.some(
    (m) => m.role === 'user' && !isAgenticContinueUserText(m.content ?? ''),
  )
  if (hasVisibleUser) return live
  const users = sent.filter(
    (m) => m.role === 'user' && !isAgenticContinueUserText(m.content ?? ''),
  )
  if (users.length === 0) return live
  return [...users, ...live]
}

/** Distance from end of scroll that counts as true bottom (hide Jump, re-engage follow). */
export const JUMP_TRUE_BOTTOM_PX = 48

/**
 * Hide Jump-to-bottom when the thread does not overflow, or the viewport is
 * already at the true bottom. A long seed can set isNearBottom=false during
 * layout; sticky collapse then removes overflow with no scrollbar, but Jump
 * stayed up (prod 2026-08-25 Update Work Spec).
 */
export function shouldHideJumpToBottom(opts: {
  scrollHeight: number
  clientHeight: number
  scrollTop: number
  trueBottomPx?: number
}): boolean {
  const band = opts.trueBottomPx ?? JUMP_TRUE_BOTTOM_PX
  const overflow = opts.scrollHeight - opts.clientHeight
  if (overflow <= band) return true
  const distance = opts.scrollHeight - (opts.scrollTop + opts.clientHeight)
  return distance < band
}

export function hasLaterAssistant(messages: Message[], index: number): boolean {
  for (let i = index + 1; i < messages.length; i += 1) {
    if (messages[i]?.role === 'assistant') return true
  }
  return false
}

/**
 * Plan-mode Build CTA: still sent to the model / persisted as a user row, but the UI
 * renders muted harness chrome instead of a blue user bubble.
 */
export const PLAN_BUILD_USER_TEXT =
  'Build: implement the plan above. Create any documents or files you drafted in this conversation using tools - do not only show them as code blocks.'

export function isPlanBuildUserText(content: string): boolean {
  const t = content.trim()
  return t === PLAN_BUILD_USER_TEXT || t.startsWith('Build: implement the plan above.')
}

export function indexOfLastAssistant(messages: Message[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') return i
  }
  return -1
}

/**
 * Streaming merges text into this index. When tools trail the assistant (common in agentic
 * rounds), the last array element is a tool row - still merge into the prior assistant slot
 * so `cappedAt` / Continue CTA survive stream end (see tool_budget_exceeded UX).
 *
 * When `streamSlotId` is set, only that live segment may be updated - never an earlier
 * capped assistant (Continue must not overwrite prior prose).
 */
export function indexOfStreamingAssistantSlot(
  messages: Message[],
  streamSlotId?: string | null,
): number {
  if (streamSlotId) {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'assistant' && messages[i].streamSlotId === streamSlotId) {
        return i
      }
    }
    return -1
  }
  const n = messages.length
  if (n === 0) return -1
  if (messages[n - 1]?.role === 'assistant') return n - 1
  return indexOfLastAssistant(messages)
}

function actionChoicesEqual(a: ActionChoice[], b: ActionChoice[]): boolean {
  if (a.length !== b.length) return false
  return a.every(
    (choice, idx) =>
      choice.label === b[idx]?.label &&
      choice.action === b[idx]?.action &&
      choice.message === b[idx]?.message,
  )
}

/**
 * Hide duplicate action-choice rows on later assistant segments in the same turn
 * (streaming leak before segment seal fix).
 */
export function messageForAssistantDisplay(messages: Message[], index: number): Message {
  const message = messages[index]
  if (message.role !== 'assistant' || !message.actionChoices?.length) return message
  for (let j = index - 1; j >= 0; j--) {
    const prev = messages[j]
    if (prev.role === 'user') break
    if (
      prev.role === 'assistant' &&
      prev.actionChoices?.length &&
      actionChoicesEqual(prev.actionChoices, message.actionChoices)
    ) {
      return { ...message, actionChoices: undefined }
    }
  }
  return message
}

/** First path segment that is a lowercase UUID (document id accidentally used as `path` on paste). */
const DOC_UUID_FIRST_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Top content-root segment for chat scope (matches workspace `contentRoot`, e.g. hop-studios, flownotes-app-docs). */
export function chatContentRootFromRefPath(filePath: string | undefined): string | null {
  if (!filePath?.trim()) return null
  let n = filePath.replace(/^\/+/, '').trim()
  if (n.startsWith('docs/')) n = `flownotes-app-docs/${n.slice(5)}`
  const parts = n.split('/').filter(Boolean)
  const seg = parts[0]
  if (!seg) return null
  // Pasted refs may set path to document UUID when filePath was missing - not a filesystem root.
  if (DOC_UUID_FIRST_SEGMENT.test(seg)) return null
  // Opaque cloud roots are `ws/{32hex}/...` - never treat bare `ws` as the root.
  if (seg === 'ws' && parts[1] && /^[a-f0-9]{32}$/i.test(parts[1])) {
    return `ws/${parts[1].toLowerCase()}`
  }
  return seg
}

function normalizeChatContentRoot(root: string): string {
  return root.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')
}

/**
 * Map a document storage path to a workspace contentRoot for chat scope widening.
 * Prefers the org's stored contentRoot (trailing-slash form) so it matches sidebar scope paths.
 * Also maps URL slug / display-name first segments to contentRoot so cold deep links
 * do not toast twice (slug then ws/hex).
 */
export function chatScopeRootFromDocumentPath(
  filePath: string | undefined,
  workspaces: Array<{
    contentRoot?: string | null
    slug?: string | null
    name?: string | null
  }>,
): string | null {
  const seg = chatContentRootFromRefPath(filePath)
  if (!seg) return null
  const normSeg = normalizeChatContentRoot(seg)
  const match = workspaces.find((w) => {
    if (w.contentRoot && normalizeChatContentRoot(w.contentRoot) === normSeg) {
      return true
    }
    if (w.slug && w.slug === seg) return true
    const name = w.name?.trim()
    if (name && (name === seg || name.toLowerCase() === seg.toLowerCase())) return true
    return false
  })
  return match?.contentRoot?.trim() || seg
}

/** Labels for "scope widened" toast (prefer workspace display name over raw content root slug). */
export function formatChatScopeWidenLabels(
  roots: string[],
  workspaces: Array<{ contentRoot?: string | null; name?: string | null; slug?: string | null }>,
): string {
  const labels = roots.map((root) => {
    const norm = normalizeChatContentRoot(root)
    const w = workspaces.find((x) => {
      if (x.contentRoot && normalizeChatContentRoot(x.contentRoot) === norm) return true
      if (x.slug && x.slug === root) return true
      const name = x.name?.trim()
      if (name && (name === root || name.toLowerCase() === root.toLowerCase())) return true
      return false
    })
    const name = w?.name?.trim()
    return name || w?.slug || norm || root
  })
  return [...new Set(labels)].join(', ')
}

type IngestionTerminalMessage = {
  content: string
  metadata?: { type?: string }
}

/** Strip legacy U+FFFD / corrupted emoji prefixes from ingestion chat copy. */
export function sanitizeIngestionDisplayText(text: string): string {
  return text
    .replace(/\uFFFD/g, ' - ')
    .replace(/^\?\s+\*\*Todo ingested successfully!\*\*/m, '**Todo ingested successfully!**')
}

export function isIngestionSuccessMessage(m: IngestionTerminalMessage): boolean {
  if (m.metadata?.type === 'ingestion-success') return true
  if (/^\?\s+\*\*Todo ingested successfully!\*\*/m.test(m.content)) return true
  if (/\*\*Todo ingested successfully!\*\*/i.test(m.content)) return true
  return m.content.includes('✅') && m.content.includes('successfully')
}

export function isIngestionErrorMessage(m: IngestionTerminalMessage): boolean {
  if (m.metadata?.type === 'ingestion-error') return true
  return (
    m.content.includes('❌') ||
    /\*\*Ingestion failed\*\*/i.test(m.content) ||
    /ingestion failed/i.test(m.content)
  )
}

/** First assistant row that looks like Basecamp ingestion completion (success or error). */
export function findIngestionTerminalIndex(msgs: IngestionTerminalMessage[]): number {
  return msgs.findIndex(
    (m) => isIngestionSuccessMessage(m) || isIngestionErrorMessage(m),
  )
}

export function isIngestionNarrativeMessage(m: Message): boolean {
  const meta = m.metadata as { type?: string } | undefined
  return meta?.type === 'ingestion-narrative'
}

export function messageLooksLikeIngestionAssistant(m: Message): boolean {
  const meta = m.metadata as { type?: string } | undefined
  return (
    isIngestionNarrativeMessage(m) ||
    isIngestionSuccessMessage(m) ||
    isIngestionErrorMessage(m) ||
    meta?.type === 'ingestion-success' ||
    meta?.type === 'ingestion-error'
  )
}

/**
 * Collect consecutive assistant rows that are ingestion progress/terminal only.
 * Stops before follow-up agent chat so "Got both IDs." style prose is not trapped
 * inside a forever-running IngestionRunCard.
 */
export function collectIngestionAssistantGroup(
  messages: Message[],
  startIdx: number,
): Message[] {
  const start = messages[startIdx]
  if (start?.role !== 'assistant' || !messageLooksLikeIngestionAssistant(start)) {
    return []
  }
  const group: Message[] = [start]
  let j = startIdx + 1
  while (
    j < messages.length &&
    messages[j].role === 'assistant' &&
    messageLooksLikeIngestionAssistant(messages[j])
  ) {
    group.push(messages[j])
    j++
  }
  return group
}

/**
 * Only collapse assistant rows into IngestionRunCard when the starting row is
 * actually ingestion progress (not follow-up agent chat in a flipped ingestion thread).
 */
export function shouldGroupAssistantMessagesAsIngestionRun(
  messages: Message[],
  startIdx: number,
): boolean {
  return collectIngestionAssistantGroup(messages, startIdx).length > 0
}

/** True when messages look like a Basecamp ingestion thread (narrative steps + optional terminal row). */
export function messagesLookLikeIngestionRun(messages: Message[]): boolean {
  const assistants = messages.filter((m) => m.role === 'assistant')
  if (assistants.length < 2) return false
  return assistants.some(
    (m) =>
      isIngestionNarrativeMessage(m) ||
      isIngestionSuccessMessage(m) ||
      isIngestionErrorMessage(m) ||
      m.metadata?.type === 'ingestion-success' ||
      m.metadata?.type === 'ingestion-error',
  )
}

/**
 * One timestamp per assistant reply: show only on the last assistant segment before the next
 * user turn (or end of list). Hide mid-turn segments (before tools or another assistant row)
 * and the active slot while streaming is still in flight.
 */
export function shouldShowAssistantTimestamp(
  messages: Message[],
  index: number,
  options?: { isStreaming?: boolean },
): boolean {
  const msg = messages[index]
  if (msg?.role !== 'assistant') return false

  const next = messages[index + 1]
  if (next?.role === 'tool' || next?.role === 'assistant') return false

  if (options?.isStreaming && index === messages.length - 1) return false

  return Boolean(msg.timestamp)
}

/**
 * Filter out XML-like tool call syntax from assistant messages.
 * Only strips snake_case tags (e.g. <search_documents>) so normal HTML is preserved.
 */
export function filterToolCallSyntax(content: string): string {
  const toolCallPattern =
    /<([a-zA-Z][a-zA-Z0-9]*_[a-zA-Z0-9_]*)>[\s\S]*?<\/\1>|<([a-zA-Z][a-zA-Z0-9]*_[a-zA-Z0-9_]*)\s*\/>/g
  const parts = content.split(/(```[\s\S]*?```)/g)
  const filtered = parts
    .map((part, i) => (i % 2 === 1 ? part : part.replace(toolCallPattern, '')))
    .join('')
  return filtered.replace(/\n{3,}/g, '\n\n').trim()
}

/**
 * Map message index -> joined prose for the whole assistant turn (for turn-level Copy).
 * Only the last assistant message in each turn gets an entry.
 */
export function buildAssistantTurnCopyByIndex(messages: Message[]): Map<number, string> {
  const result = new Map<number, string>()
  let indices: number[] = []
  let parts: string[] = []

  const flush = () => {
    if (indices.length === 0) return
    const joined = parts
      .map((p) => filterToolCallSyntax(p))
      .filter((p) => p.length > 0)
      .join('\n\n')
    if (joined) {
      result.set(indices[indices.length - 1], joined)
    }
    indices = []
    parts = []
  }

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]
    if (msg.role === 'user') {
      flush()
      continue
    }
    if (msg.role === 'assistant' && typeof msg.content === 'string' && msg.content.trim()) {
      indices.push(i)
      parts.push(msg.content)
    }
  }
  flush()
  return result
}

/**
 * Process content to replace document IDs with clickable markdown links
 */
export function processContentWithDocumentChips(
  content: string,
  documentsMap?: Map<string, DocumentReference>
): string {
  // Guard: persisted/streamed payloads can hand non-strings; never throw in render.
  let processed = typeof content === 'string' ? content : String(content ?? '')
  
  if (documentsMap && documentsMap.size > 0) {
    documentsMap.forEach((doc, rawId) => {
      const id = typeof rawId === 'string' ? rawId : String(rawId ?? '')
      if (!id) return
      const title =
        typeof doc?.title === 'string' && doc.title.trim()
          ? doc.title
          : `Document ${id.slice(0, 8)}...`
      const escapedId = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      
      // Pattern 1: "(Document ID: xxx)" - matches the AI's format exactly
      const parenPattern = new RegExp(`\\(\\s*Document ID:?\\s*${escapedId}\\s*\\)`, 'gi')
      processed = processed.replace(parenPattern, () => `([${title}](doc:${id}))`)
      
      // Pattern 2: "Document ID: xxx" (standalone, not in parentheses)
      const idPattern = new RegExp(`Document ID:?\\s+${escapedId}(?!\\s*\\))`, 'gi')
      processed = processed.replace(idPattern, () => `[${title}](doc:${id})`)
      
      // Pattern 3: Partial ID matching - "Document 8c2f4e5a..." (first 8 chars)
      const partialId = id.substring(0, 8)
      const partialEscaped = partialId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      // Match "Document 8c2f4e5a..." or "Document 8c2f4e5a" (with or without ellipsis)
      const partialPattern = new RegExp(`Document\\s+${partialEscaped}[a-f0-9]*\\.\\.\\.?`, 'gi')
      processed = processed.replace(partialPattern, () => `[${title}](doc:${id})`)
      
      // Pattern 4: Partial ID in parentheses - "(Document 8c2f4e5a...)"
      const partialParenPattern = new RegExp(`\\(\\s*Document\\s+${partialEscaped}[a-f0-9]*\\.\\.\\.?\\s*\\)`, 'gi')
      processed = processed.replace(partialParenPattern, () => `([${title}](doc:${id}))`)
    })
  }
  
  // Fallback 1: Make any full UUID in "(Document ID: xxx)" format clickable
  processed = processed.replace(
    /\(Document ID:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*\)/gi,
    (_match, uuid) => {
      const doc = documentsMap?.get(uuid)
      const title = doc?.title || `Document ${uuid.substring(0, 8)}...`
      return `([${title}](doc:${uuid}))`
    }
  )
  
  // Fallback 2: Try to match partial IDs (8+ hex chars) and search for full UUID
  // This handles cases where AI uses partial IDs like "8c2f4e5a..."
  if (documentsMap && documentsMap.size > 0) {
    processed = processed.replace(
      /(?:\(|^|\s)Document\s+([0-9a-f]{8,})(?:\.\.\.)?(?:\s*\)|$|\s)/gi,
      (match, partialId) => {
        // Try to find a document whose ID starts with this partial ID
        for (const [rawFullId, doc] of documentsMap.entries()) {
          const fullId = typeof rawFullId === 'string' ? rawFullId : String(rawFullId ?? '')
          if (!fullId || !fullId.toLowerCase().startsWith(partialId.toLowerCase())) continue
          const title =
            typeof doc?.title === 'string' && doc.title.trim()
              ? doc.title
              : `Document ${fullId.slice(0, 8)}...`
          // Check if it's in parentheses
          const isInParens = match.trim().startsWith('(')
          if (isInParens) {
            return `([${title}](doc:${fullId}))`
          }
          return `[${title}](doc:${fullId})`
        }
        // No match found, return original
        return match
      }
    )
  }
  
  return processed
}

/** Short human-readable tool name for UI and toasts (no emojis). */
const TOOL_LABELS: Record<string, string> = {
  searchDocuments: 'Search documents',
  getDocument: 'Read document',
  updateDocument: 'Update document',
  appendToDocument: 'Append to document',
  updateDocumentFrontmatter: 'Update metadata',
  createDocument: 'Create document',
  archiveDocument: 'Archive document',
  moveDocument: 'Move document',
  fetchWebPage: 'Fetch web page',
  webSearch: 'Web search',
  imageSearch: 'Image search',
  suggestActions: 'Suggest actions',
  fetchYoutubeTranscript: 'YouTube transcript',
  searchYoutube: 'YouTube search',
  getBasecampAssignments: 'Basecamp assignments',
  fetchBasecampTodo: 'Basecamp todo',
  fetchBasecampRecording: 'Basecamp recording',
  updateBasecampTodoNotes: 'Update Basecamp notes',
  queryBasecampTodos: 'Query Basecamp todos',
  searchBasecamp: 'Search Basecamp',
  getBasecampTodoComments: 'Basecamp comments',
  getTodoContext: 'Get todo context',
  readToolResult: 'Read tool result',
}

function humanizeToolName(toolName: string): string {
  const spaced = toolName.replace(/([a-z])([A-Z])/g, '$1 $2')
  return spaced
    .split(' ')
    .map((w) => {
      const lower = w.toLowerCase()
      if (lower === 'youtube') return 'YouTube'
      if (lower === 'basecamp') return 'Basecamp'
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    })
    .join(' ')
}

/**
 * Human-readable tool label for inline UI and toasts.
 */
export function getToolLabel(toolName: string): string {
  return TOOL_LABELS[toolName] ?? humanizeToolName(toolName)
}

/**
 * Normalize changed lines payload from various formats
 */
export function normalizeChangedLines(raw: any): {
  added: number[]
  removed: number[]
  modified: number[]
  changed_at?: string
  changed_by?: string
  change_status?: string
  requires_acceptance?: boolean
  accepted_at?: string | null
  conversation_id?: string | null
} | null {
  if (!raw) return null
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') {
    return null
  }

  const ensureNumberArray = (arr: any): number[] => {
    if (!Array.isArray(arr)) return []
    return arr
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0)
  }

  return {
    added: ensureNumberArray(value.added),
    removed: ensureNumberArray(value.removed),
    modified: ensureNumberArray(value.modified),
    changed_at: value.changed_at,
    changed_by: value.changed_by,
    change_status: value.change_status,
    requires_acceptance: value.requires_acceptance,
    accepted_at: value.accepted_at ?? null,
    conversation_id: value.conversation_id ?? null,
  }
}


